// src/utils/prazoSimulatorHelper.js
import { getModelFromSku } from "./skuUtils";
import {
  addBusinessDays,
  normalizeToNoon,
  formatDateToIso,
} from "./formatters";
import {
  formatDataComDiaSemana,
  formatDiasUteis,
  round1,
  ETAPAS_CANONICAS,
} from "./prazoEngine";

/**
 * Nomes amigáveis para as etapas produtivas
 */
export const ETAPAS_LABELS = {
  SOLICITACAO: "Aguardando Análise",
  MODELAGEM: "Modelagem 3D",
  IMPRIMINDO: "Impressão 3D",
  CURA: "Cura UV",
  FUNDICAO: "Fundição",
  BANCA: "Bancada & Cravação",
  POLIMENTO: "Polimento",
  RESINA_FINALIZACAO: "Acabamento & Resina",
};

/**
 * Helper para busca insensível a maiúsculas/minúsculas em dicionários
 */
function findCaseInsensitive(dict = {}, key = "") {
  if (!dict || !key) return null;
  if (dict[key] !== undefined) return dict[key];
  const target = String(key).trim().toLowerCase();
  for (const k of Object.keys(dict)) {
    if (k.trim().toLowerCase() === target) {
      return dict[k];
    }
  }
  return null;
}

/**
 * Simula os prazos e datas de um item individual a ser produzido
 *
 * @param {Object} params
 * @param {string} params.sku - SKU do produto desejado
 * @param {string} [params.modelo] - Modelo explícito ou sobrescrito
 * @param {Date|string} [params.dataInicio] - Data marco zero do pedido (default: hoje)
 * @param {Object} [params.prazosMap] - Dicionário modelo -> dados do modelo (prazo_prometido, prazo_medio, etc.)
 * @param {Object} [params.temposEtapaMap] - Dicionário modelo -> etapa -> dias úteis restantes
 * @param {Object} [params.catalogItem] - Objeto com informações do catálogo (título, imagem, etc.)
 * @returns {Object} Análise simulada do item
 */
export function simularPrazosItem({
  sku = "",
  modelo = null,
  dataInicio = new Date(),
  prazosMap = {},
  temposEtapaMap = {},
  catalogItem = null,
}) {
  const cleanSku = String(sku || "").trim().toUpperCase();
  const modeloResolvido = modelo || getModelFromSku(cleanSku) || "Geral";

  // Busca o registro do modelo em prazosMap (case-insensitive)
  const prazoRecord =
    findCaseInsensitive(prazosMap, modeloResolvido) ||
    findCaseInsensitive(prazosMap, "Geral") ||
    null;

  const prazoPrometido = Number(prazoRecord?.prazo_prometido ?? prazoRecord) || 0;
  const prazoMedio = Number(prazoRecord?.prazo_medio) || 0;
  const prazoSugerido = Number(prazoRecord?.prazo_sugerido) || 0;
  const fatorRisco = Number(prazoRecord?.fator_risco) || 0;

  const dataBase = normalizeToNoon(dataInicio) || normalizeToNoon(new Date());

  // Mapa de etapas para o modelo (case-insensitive)
  const mapaEtapas =
    findCaseInsensitive(temposEtapaMap, modeloResolvido) ||
    findCaseInsensitive(temposEtapaMap, "Geral") ||
    temposEtapaMap ||
    {};

  // Tempo histórico de manufatura (a partir da Solicitação / etapa inicial)
  let tempoHistoricoFabricacao = round1(Number(mapaEtapas["SOLICITACAO"]) || 0);

  // Se a matriz de etapas não tiver a etapa SOLICITACAO para este modelo,
  // utiliza como fallback inteligente o prazo_medio real ou o prazo_sugerido cadastrados no modelo
  if (tempoHistoricoFabricacao <= 0) {
    if (prazoMedio > 0) {
      tempoHistoricoFabricacao = round1(prazoMedio);
    } else if (prazoSugerido > 0) {
      tempoHistoricoFabricacao = round1(prazoSugerido);
    } else if (prazoPrometido > 0) {
      // Caso não haja histórico gravado, estimativa realista é cerca de 65% do teto prometido
      tempoHistoricoFabricacao = round1(Math.max(1, prazoPrometido * 0.65));
    } else {
      tempoHistoricoFabricacao = 3.5;
    }
  }

  // Prazo estimado (dias úteis realistas)
  const prazoEstimado = tempoHistoricoFabricacao;

  // Data estimada de conclusão na fábrica (dias úteis a partir da data base)
  const dataEstimada =
    prazoEstimado > 0
      ? addBusinessDays(dataBase, Math.max(1, Math.ceil(prazoEstimado)))
      : dataBase;

  // Data prometida máxima na venda (teto com margem de segurança)
  const dataPrometida =
    prazoPrometido > 0 ? addBusinessDays(dataBase, prazoPrometido) : dataEstimada;

  // Folga / margem de segurança entre o prazo prometido na venda e o tempo histórico fabril
  const folgaEstimada =
    prazoPrometido > 0 && prazoEstimado > 0
      ? round1(Math.max(0, prazoPrometido - prazoEstimado))
      : 0;

  // Projeção do cronograma etapa a etapa
  const cronogramaEtapas = [];
  let diasAcumulados = 0;

  for (let i = 0; i < ETAPAS_CANONICAS.length; i++) {
    const etapaKey = ETAPAS_CANONICAS[i];
    const diasRestantesNestaEtapa = Number(mapaEtapas[etapaKey]);

    let duracaoEtapa = 0.5; // padrão de 0.5 d.ú.
    if (i < ETAPAS_CANONICAS.length - 1) {
      const proxEtapaKey = ETAPAS_CANONICAS[i + 1];
      const diasRestantesProx = Number(mapaEtapas[proxEtapaKey]);
      if (
        !isNaN(diasRestantesNestaEtapa) &&
        !isNaN(diasRestantesProx) &&
        diasRestantesNestaEtapa > diasRestantesProx
      ) {
        duracaoEtapa = round1(diasRestantesNestaEtapa - diasRestantesProx);
      }
    } else if (!isNaN(diasRestantesNestaEtapa) && diasRestantesNestaEtapa > 0) {
      duracaoEtapa = round1(diasRestantesNestaEtapa);
    }

    diasAcumulados += duracaoEtapa;
    const dataConclusaoEtapa = addBusinessDays(dataBase, Math.ceil(diasAcumulados));

    cronogramaEtapas.push({
      etapa: etapaKey,
      label: ETAPAS_LABELS[etapaKey] || etapaKey,
      duracaoDiasUteis: duracaoEtapa,
      diasAcumulados: round1(diasAcumulados),
      dataConclusao: dataConclusaoEtapa,
      dataConclusaoFormatada: formatDataComDiaSemana(dataConclusaoEtapa, true),
    });
  }

  return {
    sku: cleanSku,
    modelo: modeloResolvido,
    titulo: catalogItem?.name || catalogItem?.titulo || catalogItem?.title || cleanSku,
    imagem: catalogItem?.imagem || catalogItem?.image || catalogItem?.foto || null,
    // Prazos em dias úteis
    prazoEstimado,
    prazoEstimadoFormatado: formatDiasUteis(prazoEstimado),
    prazoPrometido,
    prazoPrometidoFormatado: formatDiasUteis(prazoPrometido),
    tempoHistoricoFabricacao,
    folgaEstimada,
    folgaEstimadaFormatada: formatDiasUteis(folgaEstimada),
    fatorRisco,
    // Datas calculadas
    dataInicio: dataBase,
    dataInicioFormatada: formatDataComDiaSemana(dataBase),
    dataEstimada,
    dataEstimadaFormatada: formatDataComDiaSemana(dataEstimada),
    dataPrometida,
    dataPrometidaFormatada: formatDataComDiaSemana(dataPrometida),
    // Compatibilidade reversa
    dataEstimadaHistorica: dataEstimada,
    dataEstimadaHistoricaFormatada: formatDataComDiaSemana(dataEstimada),
    cronogramaEtapas,
  };
}

/**
 * Simula um pedido completo composto por 1 ou mais itens
 *
 * @param {Object} params
 * @param {Array<Object>} params.itens - Lista de itens ({ sku, modelo, quantidade, catalogItem })
 * @param {Date|string} [params.dataInicio] - Data marco zero da simulação
 * @param {Object} [params.prazosMap] - Dicionário modelo -> prazo
 * @param {Object} [params.temposEtapaMap] - Dicionário tempos de etapa
 * @returns {Object} Diagnóstico global da simulação do pedido
 */
export function simularPrazosPedido({
  itens = [],
  dataInicio = new Date(),
  prazosMap = {},
  temposEtapaMap = {},
}) {
  const dataBase = normalizeToNoon(dataInicio) || normalizeToNoon(new Date());

  if (!Array.isArray(itens) || itens.length === 0) {
    return {
      itensSimulados: [],
      prazoEstimadoPedido: 0,
      prazoEstimadoPedidoFormatado: "0",
      prazoPrometidoPedido: 0,
      prazoPrometidoPedidoFormatado: "0",
      folgaPedido: 0,
      folgaPedidoFormatada: "0",
      tempoHistoricoPedido: 0,
      dataInicio: dataBase,
      dataInicioFormatada: formatDataComDiaSemana(dataBase),
      dataEstimadaPedido: dataBase,
      dataEstimadaPedidoFormatada: formatDataComDiaSemana(dataBase),
      dataPrometidaPedido: dataBase,
      dataPrometidaPedidoFormatada: formatDataComDiaSemana(dataBase),
      itemCritico: null,
      itemCriticoEstimado: null,
      totalItens: 0,
    };
  }

  // Simula cada item individualmente
  const itensSimulados = itens.map((item) =>
    simularPrazosItem({
      sku: item.sku,
      modelo: item.modelo,
      dataInicio: dataBase,
      prazosMap,
      temposEtapaMap,
      catalogItem: item.catalogItem,
    })
  );

  // Gargalo pelo prazo prometido máximo e pelo tempo estimado da fábrica
  let itemCritico = itensSimulados[0];
  let itemCriticoEstimado = itensSimulados[0];
  let maxPrazoPrometido = itemCritico.prazoPrometido;
  let maxPrazoEstimado = itemCritico.prazoEstimado;

  for (const it of itensSimulados) {
    if (it.prazoPrometido > maxPrazoPrometido) {
      maxPrazoPrometido = it.prazoPrometido;
      itemCritico = it;
    }
    if (it.prazoEstimado > maxPrazoEstimado) {
      maxPrazoEstimado = it.prazoEstimado;
      itemCriticoEstimado = it;
    }
  }

  // Data estimada do pedido (baseada no item com maior tempo de fabricação)
  const dataEstimadaPedido =
    maxPrazoEstimado > 0
      ? addBusinessDays(dataBase, Math.max(1, Math.ceil(maxPrazoEstimado)))
      : dataBase;

  // Data prometida do pedido (teto comercial garantido ao cliente)
  const dataPrometidaPedido =
    maxPrazoPrometido > 0
      ? addBusinessDays(dataBase, maxPrazoPrometido)
      : dataEstimadaPedido;

  // Margem de segurança do pedido completo
  const folgaPedido = round1(Math.max(0, maxPrazoPrometido - maxPrazoEstimado));

  return {
    itensSimulados,
    // Prazo Estimado da Fábrica (Tempo Realista)
    prazoEstimadoPedido: maxPrazoEstimado,
    prazoEstimadoPedidoFormatado: formatDiasUteis(maxPrazoEstimado),
    dataEstimadaPedido,
    dataEstimadaPedidoFormatada: formatDataComDiaSemana(dataEstimadaPedido),
    // Prazo Máximo Prometido ao Cliente (Margem de Segurança)
    prazoPrometidoPedido: maxPrazoPrometido,
    prazoPrometidoPedidoFormatado: formatDiasUteis(maxPrazoPrometido),
    dataPrometidaPedido,
    dataPrometidaPedidoFormatada: formatDataComDiaSemana(dataPrometidaPedido),
    // Folga e métricas
    folgaPedido,
    folgaPedidoFormatada: formatDiasUteis(folgaPedido),
    tempoHistoricoPedido: maxPrazoEstimado,
    dataInicio: dataBase,
    dataInicioFormatada: formatDataComDiaSemana(dataBase),
    itemCritico,
    itemCriticoEstimado,
    totalItens: itensSimulados.length,
  };
}

/**
 * Gera texto formatado profissional pronto para envio via WhatsApp ou Atendimento ao Cliente
 *
 * @param {Object} params
 * @param {Array<Object>} params.itensSimulados
 * @param {number} params.prazoEstimadoPedido - Prazo estimado realista da fábrica
 * @param {number} params.prazoPrometidoPedido - Prazo máximo prometido ao cliente
 * @param {string} params.dataEstimadaPedidoFormatada - Data prevista realista
 * @param {string} params.dataPrometidaPedidoFormatada - Data limite de segurança
 * @param {string} params.dataInicioFormatada - Data início formatada
 * @param {string} [params.modo="completo"] - "completo" | "prometido" | "estimado"
 */
export function gerarMensagemWhatsAppSimulacao({
  itensSimulados = [],
  prazoEstimadoPedido = 0,
  prazoPrometidoPedido = 0,
  dataEstimadaPedidoFormatada = "-",
  dataPrometidaPedidoFormatada = "-",
  dataInicioFormatada = "-",
  modo = "completo",
}) {
  if (!itensSimulados || itensSimulados.length === 0) {
    return "";
  }

  // Linhas dos produtos simulados
  const linhasProdutos = itensSimulados
    .map((it, idx) => {
      const num = itensSimulados.length > 1 ? `${idx + 1}. ` : "";
      if (modo === "estimado") {
        return `💎 *${num}${it.titulo}* (SKU: \`${it.sku}\`)\n   └ Modelo: *${it.modelo}* • Estimativa: *~${it.prazoEstimadoFormatado} dias úteis*`;
      }
      if (modo === "prometido") {
        return `💎 *${num}${it.titulo}* (SKU: \`${it.sku}\`)\n   └ Modelo: *${it.modelo}* • Prazo: *${it.prazoPrometidoFormatado} dias úteis*`;
      }
      // Modo completo (exibe estimado + limite de segurança)
      return `💎 *${num}${it.titulo}* (SKU: \`${it.sku}\`)\n   └ Modelo: *${it.modelo}* • Estimativa: *~${it.prazoEstimadoFormatado} d.ú.* (Prazo Limite: ${it.prazoPrometidoFormatado} d.ú.)`;
    })
    .join("\n\n");

  let blocoPrazos = "";

  if (modo === "estimado") {
    blocoPrazos = `🗓️ *Data de Início da Ordem:* ${dataInicioFormatada}
⏱️ *Prazo Estimado de Fabricação:* *~${formatDiasUteis(prazoEstimadoPedido)} dias úteis*
📅 *Previsão de Conclusão na Fábrica:* *${dataEstimadaPedidoFormatada}*`;
  } else if (modo === "prometido") {
    blocoPrazos = `🗓️ *Data de Início da Ordem:* ${dataInicioFormatada}
⏱️ *Prazo de Produção Prometido:* *${formatDiasUteis(prazoPrometidoPedido)} dias úteis*
📅 *Data Limite de Conclusão:* *${dataPrometidaPedidoFormatada}*`;
  } else {
    // Modo completo
    blocoPrazos = `🗓️ *Data de Início da Ordem:* ${dataInicioFormatada}
⏱️ *Estimativa de Fabricação:* *~${formatDiasUteis(prazoEstimadoPedido)} dias úteis* (Previsão: *${dataEstimadaPedidoFormatada}*)
🛡️ *Prazo Máximo Prometido:* *${formatDiasUteis(prazoPrometidoPedido)} dias úteis* (Limite Seguro: *${dataPrometidaPedidoFormatada}*)`;
  }

  return `✨ *Previsão de Produção — Sempre Joias* ✨

Olá! Conforme solicitado, segue a previsão de fabricação da sua joia:

${linhasProdutos}

${blocoPrazos}

ℹ️ *Como funcionam nossos prazos:*
• O prazo é contado em dias úteis após a confirmação do pagamento e especificações.
• Finais de semana e feriados em São Paulo não contam como dias de bancada.
• Assim que sua joia estiver pronta na fábrica, realizamos a conferência final e o envio seguro!

Qualquer dúvida, estamos à disposição! 💍✨`.trim();
}

